import { VehicleFoundSchemaType } from './../direct-flow/pages/VehicleFound/schema/schema'

export enum VehicleOwnerType {
  POLICY_HOLDER = 'POLICY_HOLDER',
  SPOUSE = 'SPOUSE',
  CIVIL_PARTNER = 'CIVIL_PARTNER',
  COHABITING_PARTNER = 'COHABITING_PARTNER',
  PARENT = 'PARENT',
  CHILD = 'CHILD',
  SIBLING = 'SIBLING',
  COMPANY = 'COMPANY',
  PRIVATE_LEASING_COMPANY = 'PRIVATE_LEASING_COMPANY',
  COMPANY_LEASING = 'COMPANY_LEASING',
  SOCIETY_OR_CLUB = 'SOCIETY_OR_CLUB',
  OTHER_FAMILY = 'OTHER_FAMILY',
  OTHER = 'OTHER',
}

export enum VehicleRegisteredKeeper {
  POLICY_HOLDER = 'POLICY_HOLDER',
  SPOUSE = 'SPOUSE',
  COHABITING_PARTNER = 'COHABITING_PARTNER',
  PARENT = 'PARENT',
  PRIVATE_LEASING_COMPANY = 'PRIVATE_LEASING_COMPANY',
  EMPLOYER = 'EMPLOYER',
}

export enum OvernightLocation {
  CARPORT = 'CARPORT',
  LOCKED_COMPOUND = 'LOCKED_COMPOUND',
  LOCKED_GARAGE = 'LOCKED_GARAGE',
  OFFICE_OR_FACTORY_CAR_PARK = 'OFFICE_OR_FACTORY_CAR_PARK',
  PRIVATE_DRIVEWAY = 'PRIVATE_DRIVEWAY',
  ON_THE_ROAD_AT_HOME = 'ON_THE_ROAD_AT_HOME',
  ON_THE_ROAD_AWAY_FROM_HOME = 'ON_THE_ROAD_AWAY_FROM_HOME',
  OPEN_PUBLIC_CAR_PARK = 'OPEN_PUBLIC_CAR_PARK',
  SECURE_PUBLIC_CAR_PARK = 'SECURE_PUBLIC_CAR_PARK',
  UNLOCKED_COMPOUND = 'UNLOCKED_COMPOUND',
  UNLOCKED_GARAGE = 'UNLOCKED_GARAGE',
}

export enum UsageType {
  SDP = 'SDP',
  SDPC = 'SDPC',
  CLASS_1 = 'CLASS_1',
  CLASS_3 = 'CLASS_3',
  CARRIAGE_OF_OWN_GOODS = 'CARRIAGE_OF_OWN_GOODS',
  HAULAGE = 'HAULAGE',
}

/**
 * Note: The DVLA this as a string, which is how we validate this.
 * Any values received from the DVLA that are not in this list will be logged to Datadog.
 */
export enum VehicleTransmission {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
  AUTOMATIC = 'AUTOMATIC',
  SEQUENTIAL_AUTOMATIC = 'SEQUENTIAL_AUTOMATIC',
  CVT = 'CVT',
}

/**
 * Note: The DVLA this as a string, which is how we validate this.
 * Any values received from the DVLA that are not in this list will be logged to Datadog.
 */
export enum VehicleFuelType {
  PETROL = 'PETROL',
  DIESEL = 'DIESEL',
  ELECTRIC = 'ELECTRIC',
  PETROL_ELECTRIC = 'PETROL_ELECTRIC',
  PETROL_LPG = 'PETROL_LPG',
}

export enum VehicleType {
  CAR = 'CAR',
  VAN = 'VAN',
}

export enum SignageType {
  NONE = 'NONE',
  FRONT = 'FRONT',
  BACK = 'BACK',
  SIDES = 'SIDES',
  FRONT_AND_BACK = 'FRONT_AND_BACK',
  FRONT_AND_SIDES = 'FRONT_AND_SIDES',
  BACK_AND_SIDES = 'BACK_AND_SIDES',
  FRONT_BACK_AND_SIDES = 'FRONT_BACK_AND_SIDES',
}

export enum OtherVehicleUse {
  NAMED_DRIVER_ON_CAR = 'NAMED_DRIVER_ON_CAR',
  OWN_OR_USE_A_CAR = 'OWN_OR_USE_A_CAR',
  OWN_OR_USE_A_VAN = 'OWN_OR_USE_A_VAN',
  OWN_OR_USE_A_MOTORCYCLE = 'OWN_OR_USE_A_MOTORCYCLE',
  COMPANY_CAR_EXCLUDING_PERSONAL_USE = 'COMPANY_CAR_EXCLUDING_PERSONAL_USE',
  COMPANY_CAR_INCLUDING_PERSONAL_USE = 'COMPANY_CAR_INCLUDING_PERSONAL_USE',
}

export enum VehicleSteering {
  LHD = 'LHD',
  RHD = 'RHD',
}

export interface VehicleDetails {
  // Returned from DVLA endpoint
  abiCode: string
  make: string
  model: string
  bodyType: string | null
  fuelType: VehicleFuelType
  transmission: VehicleTransmission
  description: string
  numberOfSeats: number
  value: number
  groupRating: number | null
  engineSize: number | null
  year: number
  import: boolean
  tracker: boolean | null
  firstRegistrationDate: string
  // End returned from DVLA endpoint
  regPlate: string | null
  steering: VehicleSteering | null
  vehicleType: VehicleType
}

export interface Mileage {
  personal: number
  business: number
}
export interface Vehicle extends VehicleDetails {
  usage: UsageType
  price: number
  fuel: VehicleFuelType
  overnightParking: OvernightLocation
  mileage: Mileage
  modificationAbiCodes: Array<string>
  purchaseDate: {
    month: number
    year: number
  } | null
  registeredKeeper: VehicleOwnerType | null
  legalOwner: VehicleOwnerType | null
  van: {
    grossWeight: number
    tippingUnit: boolean
    refrigerationUnit: boolean
    internalRacking: boolean
    badDriverHotline: boolean
    carriesHazardousGoods: boolean
    visitsHazardousSites: boolean
    signage: SignageType
    trailer: Record<string, string> | null
  } | null
}

export interface VehicleQuoteV2 extends Vehicle {
  britishIslandsRegPlate: boolean
  purchased: boolean
}

export interface VehicleRegInfo extends Pick<Vehicle, 'regPlate' | 'usage' | 'vehicleType'> {
  car: string
}

export type DVLAVehicleResponse = VehicleFoundSchemaType

export interface ManuallyEnteredVehicle {
  make: string
  model: string
  manufactureYear: string
  fuelType: VehicleFuelType
  value: number
  vehicleType: VehicleType
  engineSize: string
  trim: string
  groupedTransmission: string
}

export type VehicleFoundValues = Omit<VehicleFoundSchemaType, 'regPlate' | 'price' | 'steering'> & {
  price?: number
  steering?: VehicleSteering
  regPlate?: string
}

export interface VehicleMissingValues {
  price?: boolean
  transmission?: boolean
  fuel?: boolean
}

export interface VehicleEndpointResponse {
  abiCode: string
  doors: string
  engineCapacity: number | null
  fuelType: VehicleFuelType
  groupRating: number | null
  groupedTransmission: string
  id: number
  manufacturer: string
  model: string
  registrationYears: number[]
  seats: number | null
  style: string
  transmission: VehicleTransmission
  trim: string
  vehicleType: VehicleType
}
